import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  makeStyles,
  TextField,
  Tooltip,
} from '@material-ui/core';
import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import { FormDropDown, SimpleDropdown } from '../formElements';
import { fetchAIBasedContent, fetchGptText } from '../../utility/cr-common';
import { Skeleton } from '@material-ui/lab';
import { useQalert } from '../../utility/services/customHooks';
import { FilterNone } from '@material-ui/icons';
import { copyUrlToClipboard } from '../../utility/common';
import IfComponent from '../shared/IfComponent';

const suggestionTags = ['Title', 'H1', 'Meta', 'H2', 'H3', 'H4', 'Images', 'Search Volume', 'Schema'];
export const ArticleTypes = ['Blog', 'Guide', 'Landing Page', 'Listicle'];

export const ArticleTypeToText = {
  Guide: 'a detailed guide on',
  Blog: 'blog content on',
  'Landing Page': 'landing page content on',
  Listicle: 'listicle content on',
};
export const writingStyles = [
  'Formal',
  'Authoritative',
  'Conversational',
  'Factual',
  'Casual',
  'Business Formal',
  'Creative/Descriptive',
  'Persuasive',
  'Neutral',
];
export const lengthOfArticle = ['500 Words', '1,000 Words', '1,500 Words', '2,000 Words', '3,000 Words'];
const keywordsToSuggestOptions = [5, 10, 15];

// The default leads, then by provider, strongest first. Same list as DiscoverKeywords.
const contentWritingModels = [
  { text: 'Claude Sonnet 5.5 (Anthropic)', value: 'claude-sonnet-5-5' },
  { text: 'Claude Opus 5.5 (Anthropic)', value: 'claude-opus-5-5' },
  { text: 'GPT-6.1 Sol (OpenAI)', value: 'gpt-6.1-sol' },
  { text: 'GPT-6 Luna (OpenAI)', value: 'gpt-6-luna' },
  { text: 'Gemini 3.8 Flash (Google)', value: 'gemini-3.8-flash' },
  // Grok 4.6 hidden 2026-09-02: ~15 tok/s on Bedrock, multi-minute silent waits.
  // Backend support (grok-service) stays; re-enable when speed is acceptable.
  // { text: 'Grok 4.6 (xAI)', value: 'grok-4.6' },
];

const getOptions = (opts) => {
  return opts.map((opt) => {
    return { text: opt, value: opt };
  });
};

const getCommanAndSeperatedStringFromArr = (arr: string[] = []) => {
  if (arr.length == 1) {
    return `${arr[0]} `;
  }
  return arr.reduce((acc, curr, index) => {
    if (index === arr.length - 1) {
      return `${acc} and ${curr}`;
    } else if (index === 0) {
      return curr;
    } else {
      return `${acc}, ${curr}`;
    }
  }, '');
};

export const getHeadings = (arr) => {
  if (arr.includes('H2') && arr.includes('H3') && arr.includes('H4')) {
    return '3-5 H2 headings and a few H3 and H4 headings under each H2';
  } else if (arr.includes('H2') && arr.includes('H3')) {
    return '3-5 H2 headings and a few H3 headings under each H2';
  } else if (arr.includes('H3') && arr.includes('H4')) {
    return '3-5 H3 headings and a few H4 headings under each H3';
  } else if (arr.includes('H2') && arr.includes('H4')) {
    return '3-5 H2 headings and a few H4 headings under each H2';
  } else if (arr.length == 1) {
    return `3-5 ${arr[0]} headings`;
  } else {
    return '';
  }
};
const useStyles = makeStyles({
  tooltipStyle: {
    zIndex: 9999,
  },
});

interface ChildComponentProps {
  isAutoWriteFunction?: boolean;
  isWithAllOptions?: boolean;
  hideLabelAndButtons?: boolean;
  isStreamingAutoWrite?: boolean;
  ref?: React.RefObject<any>;
  resetQuickBrief?: (boolean) => void;
  onContentFetch?: (string) => void;
  onPromptReady?: (prompt: { promptConfig: any; model: string; stream: boolean }) => void;
  _h1?: string;
  _target_keywords_list?: string[] | undefined;
  _language?: string;
  isDisplayTocOptions?: boolean;
  _setWithToc?: (flag) => void;
  /** "Include Key Takeaways": after the article is written, generate a Key Takeaways box from it. */
  _setWithKeyTakeaways?: (flag) => void;
}

const QuickBriefGenerator = forwardRef((props: ChildComponentProps, ref) => {
  const {
    isWithAllOptions = true,
    hideLabelAndButtons = false,
    isStreamingAutoWrite = false,
    isDisplayTocOptions,
  } = props;
  const classes = useStyles();
  const [showResult, setShowResult] = React.useState(false);
  const [textValue, setTextValue] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [_, toggleSnackBar] = useQalert();
  const [autoWriteLoader, setAutoWriteLoader] = React.useState(false);
  const [titleForCopyAction, setTitleForCopyAction] = React.useState('Copy');
  const [finalUnWrappedText, setFinalUnWrappedText] = React.useState('');
  const [initialFinalText, setInitialFinalText] = React.useState('');

  const [pageTopic, setPageTopic] = React.useState(props._h1 || '');
  const [articleType, setArticleType] = React.useState('Blog');
  const [writingStyle, setWritingStyle] = React.useState('Formal');
  const [articlelength, setArticlelength] = React.useState('2,000 Words');
  const [keywordsToSuggest, setKeywordsToSuggest] = React.useState('');
  const [targetAudience, setTargetAudience] = React.useState('');
  const [outcome, setOutcome] = React.useState('');
  const [additinoalInstructions, setAdditinoalInstructions] = React.useState('');
  const [isWithTocOptionsSelect, setIsWithTocOptionsSelect] = React.useState(false);
  const [isWithKeyTakeaways, setIsWithKeyTakeaways] = React.useState(false);
  const [showAdvancedSettings, setShowAdvancedSettings] = React.useState(false);
  const [contentWritingModel, setContentWritingModel] = React.useState('claude-sonnet-5-5');
  const [isStreamEnabled, setIsStreamEnabled] = React.useState(true);

  const [selectedSuggestions, setSelectedSuggestions] = React.useState<string[]>([]);

  const isValidForm = pageTopic && articleType && writingStyle && articlelength && keywordsToSuggest;

  useEffect(() => {
    setPageTopic(props?._h1 || '');
  }, [props?._h1]);

  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;
    if (checked) {
      setSelectedSuggestions([...selectedSuggestions, name]);
    } else {
      setSelectedSuggestions(selectedSuggestions.filter((option) => option !== name));
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      callAutoWriteApi,
    }),
    [
      pageTopic,
      articleType,
      writingStyle,
      articlelength,
      keywordsToSuggest,
      targetAudience,
      outcome,
      additinoalInstructions,
      selectedSuggestions,
      isStreamEnabled,
      contentWritingModel,
    ]
  );

  const callAutoWriteApi = () => {
    generateQuickBrief();
  };

  const generateQuickBrief = async () => {
    try {
      const initialHeadingSuggestions = getHeadings(selectedSuggestions.filter((i) => ['H2', 'H3', 'H4'].includes(i)));

      const otherTags = selectedSuggestions.filter((i) => !['H2', 'H3', 'H4', 'Search Volume', 'Schema'].includes(i));

      const allSelectedOptions = selectedSuggestions.join(',');

      const promptConfig = {
        articleTypeText: ArticleTypeToText[articleType] || '',
        articleType,
        pageTopic,
        targetAudience,
        target_keywords_list:
          props.isAutoWriteFunction && props._target_keywords_list && props._target_keywords_list.length
            ? props?._target_keywords_list?.join(', ')
            : '',
        writingStyle,
        articleLength: articlelength,
        // newly added
        initialHeadingSuggestions,
        language: props._language,
        outcome,
        format: 'strictly in HTML format',
        additionalInstructions: additinoalInstructions,
        suggestSchema: selectedSuggestions.includes('Schema') ? 'true' : '',
        otherTags: otherTags ? otherTags.join(',') : '',
      };
      if (props.onPromptReady) {
        props.onPromptReady({
          promptConfig,
          model: contentWritingModel,
          stream: isStreamEnabled,
        });
        return;
      }
      setLoading(true);
      promptConfig['keywordsToSuggest'] = keywordsToSuggest || 5;
      promptConfig['format'] = '';
      promptConfig['withSearchVolume'] = allSelectedOptions.includes('Search Volume')
        ? 'with monthly search volumes'
        : '';
      promptConfig['withWebPageSchemaM'] = allSelectedOptions.includes('Schema') ? 'and web page schema markup' : '';
      const res = await fetchAIBasedContent({
        promptFor: `PAGE_DRAFT.GENERATE_QUICK_BRIEF`,
        promptConfig: promptConfig,
        tokenLimit: 3500,
        isPublicApi: false,
      });
      const gptTextEffect = `<span class="gpt-text-appended">${res}</span>`;
      setTextValue(gptTextEffect);
      setShowResult(true);
      setLoading(false);
      return;
    } catch (error) {
      toggleSnackBar({ type: 'error', text: 'Oops! Something Went Wrong. Please try again.', open: true });
      setLoading(false);
      console.error('error :: generateQuickBrief', error);
    }
  };

  const generateTextForStreamingGpt = () => {
    let finalText = `Write a topic map content ${
      props._language ? `in ${props._language}` : ''
    } strictly in HTML format for a `;

    finalText = finalText + (ArticleTypeToText[articleType] || '');
    finalText = finalText + ` "${pageTopic}"${targetAudience ? ' for audience of type ' + `"${targetAudience}"` : ''}.`;

    if (props.isAutoWriteFunction && props._target_keywords_list && props._target_keywords_list.length) {
      finalText =
        finalText + ` the content should be targeting keywords: "${props?._target_keywords_list?.join(', ')}". `;
    }

    const initialheadingSuggestions = getHeadings(selectedSuggestions.filter((i) => ['H2', 'H3', 'H4'].includes(i)));

    if (initialheadingSuggestions) {
      finalText = finalText + ` The topic map would include ${initialheadingSuggestions}. `;
    }
    finalText =
      finalText +
      ` Writing style should be ${writingStyle} and Tentative Word Count of the ${articleType} should be ${articlelength}. `;

    if (outcome) {
      finalText = finalText + `Desired outcome for the reader: ${outcome}. `;
    }

    // suggest the top ${keywordsToSuggest || 5} keywords ${
    //   selectedSuggestions.includes('Search Volume') ? 'with monthly search volumes' : ''
    // } to target
    const keyWordsInput = `${
      selectedSuggestions.includes('Schema')
        ? ' suggest web page schema markup as json wrapped in pre tag (do not provide with a script tag)  Under Schema Markup Section'
        : ''
    } for this ${articleType}.`;
    finalText = finalText + keyWordsInput;

    const optionsForCall3 = selectedSuggestions.filter(
      (i) => !['H2', 'H3', 'H4', 'Search Volume', 'Schema'].includes(i)
    );
    optionsForCall3.forEach((i) => {
      finalText =
        finalText +
        ` ${
          i
            ? i === 'Images'
              ? `Include five suggestions for images in form of links or text describing the images in a list Under Images Section `
              : `Include five options for ${i}.\n`
            : ''
        }`;
    });

    if (optionsForCall3.includes('Meta')) {
      finalText =
        finalText +
        `NOTE: for Meta, include both Title and Description, meta title should be between 50-60 characters and meta description should be between 150-160 characters`;
    }

    finalText = finalText + ` ${additinoalInstructions} `;

    finalText =
      finalText +
      ` do not include DOCTYPE, HTML, HEAD, BODY tags OR more than 1 new line characters in response. all information should be strictly in html format. ${additinoalInstructions} `;

    if (props.isAutoWriteFunction && props.onPromptReady) {
      props.onPromptReady({
        promptConfig: { finalText },
        model: contentWritingModel,
        stream: isStreamEnabled,
      });
      return;
    }
  };

  const autoWriteHandler = async () => {
    try {
      const lastNewLineIndex = textValue?.lastIndexOf('\n');
      const lastLine = textValue?.slice(lastNewLineIndex + 1);
      if (lastLine) {
        setAutoWriteLoader(true);
        const result = await fetchGptText(initialFinalText + '\n' + lastLine, null, 3500);
        const gptTextEffect = `<span class="gpt-text-appended">${
          finalUnWrappedText + '\n\n' + lastLine + '\n' + result + '\n\n'
        }</span>`;
        setTextValue(gptTextEffect);
      } else {
        toggleSnackBar({
          type: 'warning',
          text: `The last line text can't be blank for Auto Write`,
          open: true,
        });
      }
    } catch (error) {
      console.error('error :: generateQuickBrief', error);
    } finally {
      setAutoWriteLoader(false);
    }
  };
  const copyHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();
    copyUrlToClipboard(textValue?.replace(/(<([^>]+)>)/gi, ''));
    setTitleForCopyAction('Copied');
    setTimeout(() => {
      setTitleForCopyAction('Copy');
    }, 1000);
  };
  return (
    <div className="quick-brief-form">
      {isWithAllOptions ? (
        <Grid style={{ padding: props.isAutoWriteFunction ? '0px' : '0px 24px' }} container>
          <IfComponent isValid={!hideLabelAndButtons}>
            <Grid item xs={8}>
              <h3 className="mt-4">Generate Quick Brief</h3>
            </Grid>
          </IfComponent>
          <Grid item xs={4}>
            {showResult && (
              <div className="additional-options m-0">
                <Button className="title p-10" variant={'outlined'} onClick={() => setShowResult(false)}>
                  Edit Properties
                </Button>
              </div>
            )}
          </Grid>
        </Grid>
      ) : null}
      <div style={{ height: '100%', overflowY: 'auto', padding: props.isAutoWriteFunction ? '0px' : '0px 24px' }}>
        <>
          {showResult && textValue ? (
            <div>
              <div
                data-testid="test-editor"
                data-placeholder="...Type a command and hit auto-write to generate content."
                className="m-0 editor pre-wrap"
                onBlur={(e) => {
                  const newtext = e?.currentTarget?.innerHTML
                    ?.replace(/<br>/gi, '\n')
                    .replace('<div>', '\n')
                    .replace(/<[^>]*>?/gm, '');
                  setTextValue(newtext || '');
                }}
                contentEditable={true}
                dangerouslySetInnerHTML={{ __html: textValue }}
              />
              {loading ? (
                <>
                  <Skeleton className="mb-10" variant="rect" height={10} />
                  <Skeleton className="mb-10" variant="rect" height={10} />
                  <Skeleton className="mb-10" variant="rect" height={10} />
                </>
              ) : null}
            </div>
          ) : (
            <>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Page Topic"
                    fullWidth
                    variant="filled"
                    value={pageTopic}
                    onChange={(e) => setPageTopic(e.target.value)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <SimpleDropdown
                    options={getOptions(ArticleTypes)}
                    id={`quick_brief__topic_dropdown_1`}
                    label="Type of Article"
                    value={articleType}
                    onChange={(e, res) => {
                      setArticleType(e.target.value);
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <SimpleDropdown
                    options={getOptions(writingStyles)}
                    id={`quick_brief__topic_dropdown_1`}
                    label="Writing Style"
                    value={writingStyle}
                    onChange={(e, res) => {
                      setWritingStyle(e.target.value);
                    }}
                  />
                </Grid>

                <Grid item xs={6}>
                  <SimpleDropdown
                    options={getOptions(lengthOfArticle)}
                    id={`quick_brief__topic_dropdown_1`}
                    label="Tentative Word Count"
                    value={articlelength}
                    onChange={(e, res) => {
                      setArticlelength(e.target.value);
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  {props.isAutoWriteFunction ? (
                    <TextField
                      label="Target Audience (Optional)"
                      fullWidth
                      value={targetAudience}
                      onChange={(e) => setTargetAudience(e.target.value)}
                      variant="filled"
                    />
                  ) : (
                    <SimpleDropdown
                      options={getOptions(keywordsToSuggestOptions)}
                      id={`quick_brief__topic_dropdown_1`}
                      label="Keywords to Suggest"
                      addNone={props.isAutoWriteFunction}
                      value={keywordsToSuggest}
                      onChange={(e, res) => {
                        setKeywordsToSuggest(e.target.value);
                      }}
                    />
                  )}
                </Grid>
                {!props.isAutoWriteFunction && (
                  <Grid item xs={12}>
                    <TextField
                      label="Target Audience (Optional)"
                      fullWidth
                      value={targetAudience}
                      onChange={(e) => setTargetAudience(e.target.value)}
                      variant="filled"
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    label="Outcome (Optional)"
                    multiline
                    rows="4"
                    fullWidth
                    value={outcome}
                    onChange={(e) => setOutcome(e.target.value)}
                    variant="filled"
                  />
                </Grid>
              </Grid>
              {isWithAllOptions ? (
                <>
                  <h3>Add Suggestions for</h3>
                  <Grid container spacing={1}>
                    {(props.isStreamingAutoWrite
                      ? suggestionTags.filter((i) => i !== 'Search Volume')
                      : suggestionTags
                    ).map((tag) => (
                      <Grid key={tag} item xs={4}>
                        <FormControlLabel
                          // id="content_recommendation__formcontrollabel__[id]_28"
                          className={''}
                          control={
                            <Checkbox
                              name={tag}
                              color="primary"
                              checked={selectedSuggestions.includes(tag)}
                              onChange={(e) => {
                                handleCheckboxChange(e);
                              }}
                            />
                          }
                          label={tag}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </>
              ) : null}
              <TextField
                label="Additional Instructions"
                multiline
                className={'mt-15'}
                rows={4}
                fullWidth
                variant="filled"
                value={additinoalInstructions}
                onChange={(e) => setAdditinoalInstructions(e.target.value)}
              />
              {isDisplayTocOptions ? (
                <div style={{ marginTop: '15px', marginBottom: '15px' }}>
                  <FormControlLabel
                    name="is_with_toc"
                    control={
                      <Checkbox
                        checked={isWithTocOptionsSelect}
                        color="primary"
                        onChange={(e) => {
                          const flag = e?.target?.checked;
                          setIsWithTocOptionsSelect(flag);
                          if (props?._setWithToc) {
                            props?._setWithToc(flag);
                          }
                        }}
                      />
                    }
                    label={'Include Table of Contents'}
                  />
                  <FormControlLabel
                    name="is_with_key_takeaways"
                    control={
                      <Checkbox
                        checked={isWithKeyTakeaways}
                        color="primary"
                        onChange={(e) => {
                          const flag = !!e?.target?.checked;
                          setIsWithKeyTakeaways(flag);
                          if (props?._setWithKeyTakeaways) {
                            props._setWithKeyTakeaways(flag);
                          }
                        }}
                        inputProps={{ 'data-testid': 'include-key-takeaways' } as any}
                      />
                    }
                    label={'Include Key Takeaways'}
                  />
                </div>
              ) : null}

              <div style={{ marginTop: '15px', marginBottom: '15px' }}>
                <Grid container alignItems="center" justifyContent="flex-end">
                  <Grid item>
                    <Button
                      variant="text"
                      color="primary"
                      size="small"
                      onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
                      style={{ textTransform: 'none' }}
                    >
                      {showAdvancedSettings ? 'Hide Advanced Settings' : 'Advanced Settings'}
                    </Button>
                  </Grid>
                </Grid>
                {showAdvancedSettings && (
                  <div style={{ marginTop: '15px' }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <SimpleDropdown
                          options={contentWritingModels}
                          id="content_writing_model_dropdown"
                          label="Content Writing Model"
                          value={contentWritingModel}
                          onChange={(e) => {
                            setContentWritingModel(e.target.value);
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <div>
                          <FormControlLabel
                            name="stream_enabled"
                            control={
                              <Checkbox
                                checked={isStreamEnabled}
                                color="primary"
                                onChange={(e) => {
                                  setIsStreamEnabled(e.target.checked);
                                }}
                              />
                            }
                            label="Stream Content Output"
                          />
                          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px', marginLeft: '32px' }}>
                            Note: Disable streaming for better content formatting. Streaming may cause list formatting
                            issues requiring manual editing.
                          </div>
                        </div>
                      </Grid>
                    </Grid>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      </div>
      {isWithAllOptions ? (
        <div className="additional-options ml-0 mt-10">
          {showResult && (
            <div
              data-testid="test-action-wrapper"
              className="action-wrapper"
              style={{ padding: '0px 4px', height: 'unset' }}
            >
              {/* eslint-disable-next-line  */}
              <Tooltip title={titleForCopyAction} classes={{ popper: classes?.tooltipStyle }} placement="top" arrow>
                <FilterNone
                  data-testid="test-action-copy"
                  className={`copy-icon ${loading ? 'disabled' : ''}`}
                  onClick={(e) => copyHandler(e)}
                />
              </Tooltip>
              <p
                data-testid="test-action-auto-write"
                className={`m-0 auto-write ${autoWriteLoader ? 'disabled' : ''}`}
                onClick={autoWriteHandler}
              >
                {autoWriteLoader ? <CircularProgress className="position-abs" size={20} /> : null}
                Auto-Write
              </p>
            </div>
          )}
          <IfComponent isValid={!hideLabelAndButtons}>
            <Grid container>
              <Grid item xs={6}>
                <Button
                  className="title p-10 mt-15"
                  variant={'outlined'}
                  onClick={() => {
                    if (props.resetQuickBrief) {
                      props.resetQuickBrief(false);
                    }
                  }}
                >
                  Cancel
                </Button>
              </Grid>
              <Grid item xs={6} className="right-align">
                <Button
                  disabled={loading || !isValidForm}
                  variant={showResult && textValue ? 'outlined' : 'contained'}
                  className="mt-15 title right-align"
                  color={!showResult ? 'primary' : undefined}
                  onClick={() => generateQuickBrief()}
                >
                  {loading && <CircularProgress className="position-abs" size={20} />}
                  {showResult && textValue ? 'Regenerate Content' : 'Generate Quick Brief'}
                </Button>
              </Grid>
            </Grid>
          </IfComponent>
        </div>
      ) : null}
    </div>
  );
});

QuickBriefGenerator.displayName = 'QuickBriefGenerator';

export default QuickBriefGenerator;
